import "./PagesDatasourcesView.styl";
import { getWaffleDatasetConfig } from "../_resources/waffle-helpers.js";
import { getSupaServerList, getSupaPageToolset } from "../_resources/supa-helpers.js";
import { supabaseClient } from "../auth/supabase.service";
import { holdButton } from "../_resources/ui-library.js";
import { renderCascadingSelectEditor } from "../_resources/cascadingSelectEditor.js";

const { observable, autorun, runInAction} = mobx;

// Helper to normalize URLs by removing trailing slash
function normalizeUrl(url) {
  return url ? url.replace(/\/$/, '') : url;
}

function renderExcelPathField(cell, {
  field, 
  idPrefix,
  selectedReader,
  currentValues,
  progress,
  good,
  bad
}) {

  let cachedExcelFiles = null;
  async function fetchExcelFiles() {
    if (cachedExcelFiles) return cachedExcelFiles;
    try {
      const { data, error } = await supabaseClient
        .from('files')
        .select('*')
        .or("name.ilike.%.xls, name.ilike.%.xlsx");
      if (error) return [];
      cachedExcelFiles = data || [];
      return cachedExcelFiles;
    } catch (e) { return []; }
  }

  async function uploadFileAndGetUrl(file) {
    if (!file) return null;
    try {
      const userRes = await supabaseClient.auth.getUser();
      const userId = userRes?.data?.user?.id || 'anon';
      const date = (new Date(Date.now())).toISOString()
        .replaceAll('-', '')
        .replace('T', '')
        .replaceAll(':', '')
        .replace(/.\d\d\dZ/, '');
      const path = `${userId}/${date}_${file.name}`;

      const { data: uploadData, error: uploadError } = await supabaseClient
        .storage
        .from('public-files')
        .upload(path, file, { upsert: false });
      if (uploadError) return null;

      let publicUrl = null;
      try {
        const res = await supabaseClient.storage.from('public-files').getPublicUrl(path);
        publicUrl = res?.publicURL || res?.data?.publicUrl || null;
      } catch (_) { publicUrl = null; }

      // Insert metadata record so file shows up in files list
      await supabaseClient.from('files').insert([{ name: file.name, path, url: publicUrl, size: file.size, content_type: file.type, user_id: userId, public: true }]);
      // refresh cached list
      cachedExcelFiles = null;
      return publicUrl;
    } catch (e) { return null; }
  }

  cell.append('label')
    .attr('for', `${idPrefix}-${field}`)
    .style('display', 'block')
    .style('margin-top', '8px')
    .style('font-weight', '500')
    .text(field);

  const input = cell.append('input')
    .attr('class', 'dynamic-field input')
    .attr('type', 'text')
    .attr('placeholder', 'Enter URL or select/upload file')
    .attr('id', `${idPrefix}-${field}`)
    .attr('value', currentValues[field] || '');

  // select existing uploaded XLSs
  const select = cell.append('select')
    .attr('class', 'input')
    .style('display', 'block')
    .style('margin-top', '6px')
    .attr('id', `${idPrefix}-${field}-select`);

  // populate select
  (async () => {
    const files = await fetchExcelFiles();
    select.append('option').attr('value', '').text('— Select uploaded XLS(X) —');
    files.forEach(f => {
      select.append('option').attr('value', f.url || f.path).text(f.name + (f.url ? '' : ' (no public url)'));
    });
    select.on('change', () => {
      const v = select.node().value;
      input.node().value = v || '';
    });
  })();

    // file upload control
  const uploadRow = cell.append('div').style('margin-top', '6px');
  const fileInput = uploadRow.append('input').attr('type', 'file').attr('accept', '.xls,.xlsx');
  const upBtn = uploadRow.append('span').attr('class', 'button').text('Upload XLS');
  upBtn.on('click', async () => {
    const file = fileInput.node().files && fileInput.node().files[0];
    if (!file) return bad('Select a file first');
    progress('Uploading...');
    const publicUrl = await uploadFileAndGetUrl(file);
    if (!publicUrl) return bad('Upload failed');
    good('Uploaded', 3000);
    input.node().value = publicUrl;
    // refresh select list
    select.node().innerHTML = '';
    const files = await fetchExcelFiles();
    select.append('option').attr('value', '').text('— Select uploaded XLS(X) —');
    files.forEach(f => select.append('option').attr('value', f.url || f.path).text(f.name));
  });

}

function renderCsvPathField(cell, {
  field, 
  idPrefix,
  selectedReader,
  currentValues,
  progress,
  good,
  bad
}) {

  let cachedCsvFiles = null;
  async function fetchCsvFiles() {
    if (cachedCsvFiles) return cachedCsvFiles;
    try {
      const { data, error } = await supabaseClient
        .from('files')
        .select('*')
        .ilike('name', '%.csv');
      if (error) return [];
      cachedCsvFiles = data || [];
      return cachedCsvFiles;
    } catch (e) { return []; }
  }

  async function uploadFileAndGetUrl(file) {
    if (!file) return null;
    try {
      const userRes = await supabaseClient.auth.getUser();
      const userId = userRes?.data?.user?.id || 'anon';
      const date = (new Date(Date.now())).toISOString()
        .replaceAll('-', '')
        .replace('T', '')
        .replaceAll(':', '')
        .replace(/.\d\d\dZ/, '');
      const path = `${userId}/${date}_${file.name}`;

      const { data: uploadData, error: uploadError } = await supabaseClient
        .storage
        .from('public-files')
        .upload(path, file, { upsert: false });
      if (uploadError) return null;

      let publicUrl = null;
      try {
        const res = await supabaseClient.storage.from('public-files').getPublicUrl(path);
        publicUrl = res?.publicURL || res?.data?.publicUrl || null;
      } catch (_) { publicUrl = null; }

      // Insert metadata record so file shows up in files list
      await supabaseClient.from('files').insert([{ name: file.name, path, url: publicUrl, size: file.size, content_type: file.type, user_id: userId, public: true }]);
      // refresh cached list
      cachedCsvFiles = null;
      return publicUrl;
    } catch (e) { return null; }
  }

  cell.append('label')
    .attr('for', `${idPrefix}-${field}`)
    .style('display', 'block')
    .style('margin-top', '8px')
    .style('font-weight', '500')
    .text(field);

  const input = cell.append('input')
    .attr('class', 'dynamic-field input')
    .attr('type', 'text')
    .attr('placeholder', 'Enter URL or select/upload file')
    .attr('id', `${idPrefix}-${field}`)
    .attr('value', currentValues[field] || '');

  // select existing uploaded CSVs
  const select = cell.append('select')
    .attr('class', 'input')
    .style('display', 'block')
    .style('margin-top', '6px')
    .attr('id', `${idPrefix}-${field}-select`);

  // populate select
  (async () => {
    const files = await fetchCsvFiles();
    select.append('option').attr('value', '').text('— Select uploaded CSV —');
    files.forEach(f => {
      select.append('option').attr('value', f.url || f.path).text(f.name + (f.url ? '' : ' (no public url)'));
    });
    select.on('change', () => {
      const v = select.node().value;
      input.node().value = v || '';
    });
  })();

  // file upload control
  const uploadRow = cell.append('div').style('margin-top', '6px');
  const fileInput = uploadRow.append('input').attr('type', 'file').attr('accept', '.csv');
  const upBtn = uploadRow.append('span').attr('class', 'button').text('Upload CSV');
  upBtn.on('click', async () => {
    const file = fileInput.node().files && fileInput.node().files[0];
    if (!file) return bad('Select a file first');
    progress('Uploading...');
    const publicUrl = await uploadFileAndGetUrl(file);
    if (!publicUrl) return bad('Upload failed');
    good('Uploaded', 3000);
    input.node().value = publicUrl;
    // refresh select list
    select.node().innerHTML = '';
    const files = await fetchCsvFiles();
    select.append('option').attr('value', '').text('— Select uploaded CSV —');
    files.forEach(f => select.append('option').attr('value', f.url || f.path).text(f.name));
  });

}


const readersFields = {
  "ddfbw": ["url", "dataset", "branch"],
  "ddfcsv": ["path"],
  "google_csv": ["path", "sheet", "isTimeInColumns", "hasNameColumn", "nameColumnIndex", "delimiter", "assetsPath"],
  "csv": ["path", "isTimeInColumns", "hasNameColumn", "nameColumnIndex", "delimiter", "assetsPath"],
  "excel": ["path", "sheet", "isTimeInColumns", "hasNameColumn", "nameColumnIndex", "delimiter", "assetsPath"]
};

const readersFieldTypes = {
  "csv": {
    "path": {
      "render": (cell, context) => renderCsvPathField(cell, context)
    },
    "nameColumnIndex": {
      "type": "number",
      "placeholder": "(0-based)"
    },
    "hasNameColumn" : {
      "type": "checkbox"
    },
    "isTimeInColumns" : {
      "type": "checkbox"
    }
  },
  "excel": {
    "path": {
      "render": (cell, context) => renderExcelPathField(cell, context)
    },
    "nameColumnIndex": {
      "type": "number",
      "placeholder": "(0-based)"
    },
    "hasNameColumn" : {
      "type": "checkbox"
    },
    "isTimeInColumns" : {
      "type": "checkbox"
    }
  },
  "google_csv": {
    "nameColumnIndex": {
      "type": "number",
      "placeholder": "(0-based)"
    },
    "hasNameColumn" : {
      "type": "checkbox"
    },
    "isTimeInColumns" : {
      "type": "checkbox"
    }
  }
}

const readersRequiredFields = {
  "ddfbw": ["url", "dataset"],
  "ddfcsv": ["path"],
  "google_csv": ["path"],
  "csv": ["path"],
  "excel": ["path"]
};

// Configuration for cascading select fields
function createCascadingConfig(bad) {
  return {
    "ddfbw": {      
      "url": { 
        label: "Server URL",
        getter: async () => {
          const {ok: serverList, error} = await getSupaServerList();
          if (error) {
            bad(error);
            return [];
          }
          return serverList; 
        },
        display: (server) => server?.url,
        getValue: (server) => server?.url?.replace(/\/$/, ''),  // Remove trailing slash
        onChange: async (selectedServer, context) => {
          context.selectedServer = selectedServer;
          const {ok: serverData, error} = await getWaffleDatasetConfig(selectedServer);
          if (error) {
            bad(error);
            return;
          }
          context.datasetBranchCommitMapping = serverData.datasetBranchCommitMapping || {};
        }
      },
      
      "dataset": {
        label: "Dataset",
        parent: "url",
        getter: async (_parentValue, context) => {
          if (!context?.datasetBranchCommitMapping) return [];
          return Object.keys(context.datasetBranchCommitMapping);
        },
        display: (dataset) => dataset,
        onChange: (selectedDataset, context) => {
          context.selectedDataset = selectedDataset;
        }
      },
      
      "branch": {
        label: "Branch",
        parent: "dataset",
        getter: async (dataset, context) => {
          if (!context?.datasetBranchCommitMapping || !dataset) return [];
          const branches = Object.keys(context.datasetBranchCommitMapping[dataset] || {});
          return branches;
        },
        display: (branch) => branch
      }
    }
  };
}


export default function ({ dom, message }) {
  const {bad, info, progress, good} = message;
  const subpanelContent = dom;
  this.hasPageEditAccess = null;
  const _this = this;

  const cascadingConfig = createCascadingConfig(bad);

  const DataSourceModel = {
    async fetch(pageId) {
      const { data, error } = await supabaseClient
        .from('pages')
        .select('datasources')
        .eq('id', pageId)
        .single();
      return { data: data?.datasources || [], error };
    },
    async add(pageId, dsId, reader, properties) {
      // Fetch current datasources array
      const { data: pageData, error: fetchError } = await supabaseClient
        .from('pages')
        .select('datasources')
        .eq('id', pageId)
        .single();
      
      if (fetchError) return { error: fetchError };
      
      const currentDatasources = pageData?.datasources || [];
      const newDatasource = { ds_id: dsId, reader, reader_properties: properties };
      const updatedDatasources = [...currentDatasources, newDatasource];
      
      const { error } = await supabaseClient
        .from('pages')
        .update({ datasources: updatedDatasources })
        .eq('id', pageId);
      
      return { error };
    },
    async update(index, pageId, updatedData) {
      // Fetch current datasources array
      const { data: pageData, error: fetchError } = await supabaseClient
        .from('pages')
        .select('datasources')
        .eq('id', pageId)
        .single();
      
      if (fetchError) return { error: fetchError };
      
      const currentDatasources = pageData?.datasources || [];
      currentDatasources[index] = { 
        ds_id: updatedData.ds_id, 
        reader: updatedData.reader, 
        reader_properties: updatedData.reader_properties 
      };
      
      const { error } = await supabaseClient
        .from('pages')
        .update({ datasources: currentDatasources })
        .eq('id', pageId);
      
      return { error };
    },
    async delete(index, pageId) {
      // Fetch current datasources array
      const { data: pageData, error: fetchError } = await supabaseClient
        .from('pages')
        .select('datasources')
        .eq('id', pageId)
        .single();
      
      if (fetchError) return { error: fetchError };
      
      const currentDatasources = pageData?.datasources || [];
      currentDatasources.splice(index, 1);
      
      const { error } = await supabaseClient
        .from('pages')
        .update({ datasources: currentDatasources })
        .eq('id', pageId);
      
      return { error };
    }
  };

  const DataSourceController = {
    async loadDatasources(pageId) {
      const { data, error } = await DataSourceModel.fetch(pageId);
      if (error) {
        subpanelContent.html('');
        subpanelContent.append('p').text('Error loading datasources.');
        console.error('Error fetching datasources', error);
        return;
      }

      // Fetch toolset to know which datasources are currently in use
      const toolsetUsage = new Map();
      const { ok: toolset } = await getSupaPageToolset(pageId);
      for (const tool of toolset) {
        const toolName = tool.id || tool.title || tool.tool || '(unnamed tool)';
        for (const dsId of (tool.dataSources || [])) {
          if (!toolsetUsage.has(dsId)) toolsetUsage.set(dsId, []);
          toolsetUsage.get(dsId).push(toolName);
        }
      }

      renderTable(data, pageId, (index, dsData, row) => renderEditForm(pageId, index, dsData, row), toolsetUsage);
    },
    async addDatasource(pageId, dsId, reader, properties) {
      const { error } = await DataSourceModel.add(pageId, dsId, reader, properties);
      if (error) console.error('Error inserting datasource', error);
      render(pageId, _this.hasPageEditAccess);
    },
    async updateDatasource(index, pageId, updatedData) {
      const { error } = await DataSourceModel.update(index, pageId, updatedData);
      if (error) console.error('Error updating datasource', error);
      render(pageId, _this.hasPageEditAccess);
    },
    async deleteDatasource(index, pageId) {
      const { error } = await DataSourceModel.delete(index, pageId);
      if (error) console.error('Error deleting datasource', error);
      render(pageId, _this.hasPageEditAccess);
    }
  };

  async function render(pageId, hasPageEditAccess) {
    _this.hasPageEditAccess = hasPageEditAccess;
    DataSourceController.loadDatasources(pageId);
  }

  function validateDatasource(dsId, reader, properties) {
    const errors = [];
    
    // Validate ds_id
    if (!dsId || dsId.trim() === '') {
      errors.push('Datasource ID is required');
    }
    
    // Validate reader
    if (!reader || reader.trim() === '') {
      errors.push('Reader type is required');
    }
    
    // Validate required properties
    const requiredFields = readersRequiredFields[reader] || [];
    requiredFields.forEach(field => {
      if (!properties[field] || properties[field].trim() === '') {
        errors.push(`${field} is required for ${reader} reader`);
      }
    });
    
    return errors;
  }


  function renderTable(dataSources, pageId, onEdit, toolsetUsage = new Map()) {
    subpanelContent.html('');

    subpanelContent.append('div').class("admin-section-header").append('h1').text('Data Sources');
    const table = subpanelContent.append('table').attr('class', 'admin-table datasource-table');
    const headerRow = table.append('tr');
    ['id', 'reader', 'properties', 'actions'].forEach(col => {
      headerRow.append('th').text(col);
    });

    const rows = table.selectAll('tr.data-row')
      .data(dataSources)
      .enter()
      .append('tr')
      .attr('class', 'data-row');

    rows.each(function(d, i) {
      const row = d3.select(this);
      row.append('td').text(d.ds_id);
      row.append('td').text(d.reader);
      
      // Display properties in defined order
      const fields = readersFields[d.reader] || [];
      const propertiesHtml = fields
        .filter(field => d.reader_properties && d.reader_properties[field] !== undefined)
        .map(field => `${field}: ${d.reader_properties[field]}`)
        .join('<br>');
      row.append('td').html(propertiesHtml || '<em>no properties</em>');
      const actionsCell = row.append('td').append('div').class("actionsbox column");
      actionsCell.append('span')
        .text('Edit')
        .attr('class', 'button edit-datasource-button')
        .classed('disabled', !_this.hasPageEditAccess)
        .on('click', () => onEdit(i, d, row));

      const usingTools = toolsetUsage.get(d.ds_id) || [];
      const isInUse = usingTools.length > 0;
      const deleteButton = actionsCell.append('span')
        .text('Delete')
        .attr('class', 'button delete hold-btn edit-datasource-button')
        .classed('disabled', !_this.hasPageEditAccess || isInUse);

      if (isInUse) {
        const inUseMsg = `"${d.ds_id}": used by tool${usingTools.length > 1 ? 's' : ''} ${usingTools.join(', ')}`;
        row.on('mouseenter.deleteWarn', () => info(inUseMsg, 3000))
           .on('click.deleteWarn', (event) => {
             if (event.target === deleteButton.node() || deleteButton.node().contains(event.target))
               info(inUseMsg, 3000);
           });
      } else {
        row.on('mouseenter.deleteWarn', () => info(`"${d.ds_id}": not used by any tool, can be deleted`, 3000));
        holdButton(deleteButton, async () => await DataSourceController.deleteDatasource(i, pageId));
      }
    });

    subpanelContent.append('div').append('span')
      .text('✚ Add Datasource')
      .attr('class', 'button addnew bigger')
      .classed('disabled', !_this.hasPageEditAccess)
      .on('click', event => {
        d3.select(event.target).remove();
        renderAddForm(pageId, table);
      });
    
    subpanelContent.append('br');
    subpanelContent.append('div').style("margin-top", "50px").append("a")
      .attr("href", "https://docs.google.com/document/d/1Ibcuzp5eaQQus515OROzkQxpcvFf3wDU03mFQmrtmV4/preview")
      .attr("target", "_blank")
      .text("Instructions for CSV and Google CSV readers");
  }

  function renderAddForm(pageId, table) {
    const form = table.append('tr')
      .attr('class', 'data-row add-form');

    form.append('td').append('input')
      .class('input')
      .attr('type', 'text')
      .attr('placeholder', 'Datasource ID')
      .attr('id', 'new-ds-id');

    const newReaderSelect = form.append('td').append('select').class('input').attr('id', 'new-reader');
    newReaderSelect.selectAll('option')
      .data(Object.keys(readersFields))
      .enter()
      .append('option')
      .attr('value', d => d)
      .text(d => d);

    const cell = form.append('td').attr('class', 'properties-cell');
    
    const propertiesEditor = renderPropertiesEditor(newReaderSelect, cell);

    const actionsCell = form.append('td')
      .class("actions-cell");
    actionsCell.append('span')
      .attr('class', 'addnew button')
      .text('Save')
      .on('click', async () => {
        const newDsId = form.select('#new-ds-id').node().value;
        const newReader = form.select('#new-reader').node().value;
        const properties = propertiesEditor.getValue();
        
        // Validate
        const errors = validateDatasource(newDsId, newReader, properties);
        if (errors.length > 0) {
          bad(errors.join('<br>'));
          return;
        }
        
        await DataSourceController.addDatasource(pageId, newDsId, newReader, properties);
      });

    actionsCell.append('span')
      .attr('class', 'button')
      .text('Cancel')
      .on('click', () => DataSourceController.loadDatasources(pageId));
  }

  function renderEditForm(pageId, index, dsData, row) {
    row.html('');
    row.classed("edit-form", true);

    row.append('td')
      .append('input')
      .class('input')
      .attr('type', 'text')
      .attr('value', dsData.ds_id)
      .attr('id', 'edit-ds-id');

    const readerCell = row.append('td');
    const newReaderSelect = readerCell.append('select').class('input').attr('id', 'edit-reader');
    newReaderSelect.selectAll('option')
      .data(Object.keys(readersFields))
      .enter()
      .append('option')
      .attr('value', d => d)
      .text(d => d);
    newReaderSelect.property('value', dsData.reader);

    const cell = row.append('td').attr('class', 'properties-cell');

    const propertiesEditor = renderPropertiesEditor(newReaderSelect, cell, dsData);

    const actionsCell = row.append('td')
      .class("actions-cell");
    actionsCell.append('span')
      .attr('class', "button")
      .text('Cancel')
      .on('click', () => DataSourceController.loadDatasources(pageId));
    actionsCell.append('span')
      .attr('class', "addnew button")
      .text('Save')
      .on('click', async () => {
        const updatedReader = row.select('#edit-reader').node().value;
        const updatedDsId = row.select('#edit-ds-id').node().value;
        const properties = propertiesEditor.getValue();
        
        // Validate
        const errors = validateDatasource(updatedDsId, updatedReader, properties);
        if (errors.length > 0) {
          bad(errors.join('<br>'));
          return;
        }
        
        const updatedData = { ds_id: updatedDsId, reader: updatedReader, reader_properties: properties};
        await DataSourceController.updateDatasource(index, pageId, updatedData);
      });

  }



  /**
   * Render dynamic form fields for datasource properties
   * @param {Object} readerSelect - D3 selection for the reader dropdown
   * @param {Object} cell - D3 selection for the properties cell
   * @param {Object} dsData - Existing datasource data (for editing)
   * @returns {Object} Object with getValue() method to retrieve properties
   */
  function renderPropertiesEditor(readerSelect, cell, dsData = null) {
    const idPrefix = dsData ? 'edit' : 'new';
    let currentEditor = null;
    
    function buildFields(selectedReader) {
      // Clear ALL existing content in the cell
      cell.html('');
      currentEditor = null;
      
      const fields = readersFields[selectedReader] || [];
      const currentValues = dsData?.reader_properties || {};
      
      // Normalize URL if present
      if (currentValues.url) {
        currentValues.url = normalizeUrl(currentValues.url);
      }
      
      // Check if this reader has cascading select configuration
      if (cascadingConfig[selectedReader]) {
        // Use cascading select editor
        currentEditor = renderCascadingSelectEditor(cell, {
          reader: selectedReader,
          schema: cascadingConfig,
          currentValues,
          idPrefix,
          onError: (error) => bad(`Error loading options: ${error.message}`)
        });
      } else {
        // Render regular input fields
        fields.forEach(field => {
          if (readersFieldTypes[selectedReader]?.[field]?.render) {
            readersFieldTypes[selectedReader][field].render(cell, {
              field,
              type: readersFieldTypes[selectedReader]?.[field]?.type || {},
              idPrefix,
              currentValues,
              selectedReader,
              progress,
              good,
              bad
            });
          } else {
            cell.append('label')
              .attr('for', `${idPrefix}-${field}`)
              .style('display', 'block')
              .style('margin-top', '8px')
              .style('font-weight', '500')
              .text(field);
              
            cell.append('input')
              .attr('class', 'dynamic-field input')
              .attr('type', readersFieldTypes[selectedReader]?.[field]?.type || 'text')
              .attr('placeholder', readersFieldTypes[selectedReader]?.[field]?.placeholder || field)
              .attr('id', `${idPrefix}-${field}`)
              .attr('value', currentValues[field] || '');
          }
        });
      }
    }
    
    // Initial render
    buildFields(readerSelect.node().value);
    
    // Re-render when reader changes
    readerSelect.on('change', () => {
      buildFields(readerSelect.node().value);
    });
    
    // Return interface to get current property values
    return {
      getValue: () => {
        const selectedReader = readerSelect.node().value;
        const fields = readersFields[selectedReader] || [];
        const properties = {};
        
        if (currentEditor) {
          // Get values from cascading select editor
          const editorValues = currentEditor.getValue();
          // Normalize URL if present
          if (editorValues.url) {
            editorValues.url = normalizeUrl(editorValues.url);
          }
          return editorValues;
        } else {
          // Get values from regular input fields
          fields.forEach(field => {
            const input = cell.select(`#${idPrefix}-${field}`).node();
            if (!input) return;
            if (input.type === 'checkbox') {
              properties[field] = !!input.checked;
            } else if (input.type === 'number') {
              const n = parseInt(input.value, 10);
              if (!Number.isNaN(n)) properties[field] = n;
            } else {
              properties[field] = input.value;
            }
          });
          return properties;
        }
      }
    };
  }

  return {render}
};

